import { useEffect, useRef, useState } from "react";

import { useTranslator } from "@eo-locale/react";

import { Box, Button, Flex, Mentions, Stack } from "@/storybook-components";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import { editComment, postComment } from "@/redux/features/brainstormers/thunk";
import { setStatus } from "@/redux/features/brainstormers/slice";
import useAuthGate from "@/hooks/useAuthGate";

import type { IdeaComments } from "@/types";

const MAX_COMMENT_LENGTH = 2000;

// react-mentions renders a mention as "@login" in the plain text value;
// the "@" itself is not counted, only the screen name is.
const getCommentLength = (plainText = "", mentions: Array<any> = []): number =>
  plainText.length - mentions.length;

// Text inserted by a single change (typing or pasting) is plain text in the
// markup value, so the overflow can be trimmed off the inserted slice without
// touching the mention markup around it. Returns null when nothing fits.
const truncateInsertion = (
  currentValue: string,
  nextValue: string,
  overflow: number
): string | null => {
  let prefix = 0;
  while (
    prefix < currentValue.length &&
    prefix < nextValue.length &&
    currentValue[prefix] === nextValue[prefix]
  ) {
    prefix += 1;
  }

  let suffix = 0;
  while (
    suffix < currentValue.length - prefix &&
    suffix < nextValue.length - prefix &&
    currentValue[currentValue.length - 1 - suffix] ===
      nextValue[nextValue.length - 1 - suffix]
  ) {
    suffix += 1;
  }

  const inserted = nextValue.slice(0, nextValue.length - suffix).slice(prefix);
  // Mention markup is inserted as a whole and cannot be cut in half.
  if (inserted.length <= overflow || /[<>]/.test(inserted)) return null;

  let end = prefix + inserted.length - overflow;
  const isHighSurrogate = (code: number) => code >= 0xd800 && code <= 0xdbff;
  if (isHighSurrogate(nextValue.charCodeAt(end - 1))) end -= 1;

  return nextValue.slice(0, end) + nextValue.slice(nextValue.length - suffix);
};

interface AllowedChange {
  value: string;
  // The resulting comment's length, so callers can track how it compares to
  // the limit without re-deriving it from the stored markup later.
  length: number;
}

const getAllowedChange = (
  nextValue: string,
  nextPlainText: string,
  nextMentions: Array<any>,
  currentValue: string,
  maxLength: number
): AllowedChange | null => {
  const nextLength = getCommentLength(nextPlainText, nextMentions);
  const overflow = nextLength - maxLength;
  if (overflow <= 0) return { value: nextValue, length: nextLength };
  // Deletions stay allowed so an already over-long comment can be brought back
  // under the limit, even while it's still over.
  if (nextValue.length < currentValue.length) {
    return { value: nextValue, length: nextLength };
  }
  const truncated = truncateInsertion(currentValue, nextValue, overflow);
  // truncateInsertion trims exactly the overflow off, so a successful result
  // always lands exactly at maxLength.
  return truncated === null ? null : { value: truncated, length: maxLength };
};

const footerButtons = (
  cancelHandler,
  cancelButtonText,
  isCancelDisabled,
  submitHandler,
  submitButtonText,
  isSubmitDisabled,
  isSubmitLoading
) => {
  return (
    <Flex
      py={6}
      alignSelf="stretch"
      justifyContent={{ base: "space-between", lg: "flex-end" }}
      w="full"
      gap={6}
    >
      <Button
        w={{ base: 160, lg: 120 }}
        size={{ base: "lg", xl: "md" }}
        maxW={160}
        px={4}
        py={2}
        variant="secondary"
        onClick={cancelHandler}
        disabled={isCancelDisabled}
      >
        {cancelButtonText}
      </Button>
      <Button
        w={{ base: 160, lg: 120 }}
        size={{ base: "lg", xl: "md" }}
        maxW={160}
        px={4}
        py={2}
        variant="primary"
        loading={isSubmitLoading}
        onClick={submitHandler}
        disabled={isSubmitDisabled}
      >
        {submitButtonText}
      </Button>
    </Flex>
  );
};
const ViewComment = ({ comment, spam }: { comment: string; spam: boolean }) => {
  const { translate } = useTranslator();
  const spamNote = translate("brainstormers.commentHidden");
  return (
    <Mentions
      value={spam ? spamNote : comment}
      showText={true}
      trigger={"@"}
      data={[]}
    />
  );
};

const CreateComment = ({
  mentions,
  setShowCommentBox
}: {
  mentions: Array<any>;
  setShowCommentBox: (show: boolean) => void;
}) => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const { suggestProfileCompletion } = useAuthGate();

  const [comment, setComment] = useState<string>("");
  const [commentLength, setCommentLength] = useState<number>(0);
  const [selectedUsers, setSelectedUsers] = useState<Array<string>>([]);

  const { status, brainstormer } = useAppSelector(
    (state) => state?.brainstormers
  );
  const maxLength = brainstormer?.commentTextLimit || MAX_COMMENT_LENGTH;
  const createCommentRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (createCommentRef?.current) {
      setTimeout(() => createCommentRef?.current?.focus(), 1000);
    }
  }, []);

  const suggestions = mentions
    ?.filter((user) => !selectedUsers.includes(user?.login))
    ?.map((user) => ({ id: user.id, display: user.login }));

  const handleChange = (_event, newValue, plainTextValue, users) => {
    const allowed = getAllowedChange(
      newValue,
      plainTextValue,
      users,
      comment,
      maxLength
    );
    if (allowed === null) {
      return;
    }
    setComment(allowed.value);
    setCommentLength(allowed.length);
    setSelectedUsers(users?.map((user) => user?.id));
  };

  const cancel = () => {
    setComment("");
    setCommentLength(0);
    setShowCommentBox(false);
  };

  const saveComment = async (comment, mentionedUsers) => {
    const response: any = await dispatch(
      postComment({ comment, mentions: mentionedUsers })
    );
    if (!!response && !response.error) {
      suggestProfileCompletion({
        reason: "profileGate.optionalSuggestion",
        source: "comment"
      });
    }
  };

  const save = async () => {
    const mentionedUsers = mentions
      .filter((mention) => selectedUsers.includes(mention.login))
      .map(({ login, id, type }) => ({
        mentioned_name: login,
        user_id: id,
        user_type: type
      }));

    saveComment(comment, mentionedUsers);
  };

  const isOverLimit = commentLength > maxLength;

  return (
    <Box
      w="100%"
      id="ehq-ideas-comment-box"
      display="inline"
      data-testid="ideas-comment-form"
    >
      <Stack gap={2} w="full">
        <Box
          as="label"
          fontSize="sm"
          fontWeight="semibold"
          lineHeight="161%"
          color="gray.800"
        >
          {translate("brainstormers.addYourText")}
        </Box>
        <Mentions
          value={comment}
          onChange={handleChange}
          inputRef={createCommentRef}
          trigger={"@"}
          data={suggestions}
        />
      </Stack>
      <Stack gap={6}>
        {footerButtons(
          cancel,
          translate("common.cancel"),
          status === "commentSaving",
          save,
          translate("common.post"),
          !comment || !comment?.trim() || isOverLimit,
          status === "commentSaving"
        )}
      </Stack>
    </Box>
  );
};

const EditComment = ({
  value,
  mentions,
  setEditComment
}: {
  value: IdeaComments;
  mentions: Array<any>;
  setEditComment: (comment: IdeaComments) => void;
}) => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();

  const [comment, setComment] = useState<string>(value?.comment || "");
  // The initial value's length isn't tracked precisely (it's markup, not
  // plain text), but that's harmless: until the first edit, comment ===
  // value.comment already disables Update regardless of isOverLimit below.
  const [commentLength, setCommentLength] = useState<number>(0);
  const [selectedUsers, setSelectedUsers] = useState<Array<string>>([]);

  const { status, comments, brainstormer } = useAppSelector(
    (state) => state?.brainstormers
  );
  const maxLength = brainstormer?.commentTextLimit || MAX_COMMENT_LENGTH;
  const editCommentRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (editCommentRef?.current) {
      setTimeout(() => {
        editCommentRef?.current?.focus();
        editCommentRef?.current?.setSelectionRange(
          comment.length,
          comment.length
        );
      }, 700);
    }
  }, []);

  useEffect(() => {
    if (status === "editCommentSaved") {
      setEditComment(undefined);
      dispatch(setStatus("idle"));
    }
  }, [status]);

  useEffect(() => {
    if (value?.comment) {
      setComment(value?.comment);
    }
  }, [comments]);

  const handleChange = (_event, newValue, plainTextValue, users) => {
    const allowed = getAllowedChange(
      newValue,
      plainTextValue,
      users,
      comment,
      maxLength
    );
    if (allowed === null) {
      return;
    }
    setComment(allowed.value);
    setCommentLength(allowed.length);
    setSelectedUsers(users?.map((user) => user?.id));
  };

  const suggestions = mentions
    ?.filter((user) => !selectedUsers.includes(user?.login))
    ?.map((user) => ({ id: user.id, display: user.login }));

  const cancel = () => {
    setEditComment(undefined);
    setComment(value?.comment);
  };
  const saveEditComment = async (commentId, comment, mentionedUsers) => {
    await dispatch(
      editComment({
        commentId: value?.id,
        comment,
        mentions: mentionedUsers
      })
    );
  };
  const save = async () => {
    const mentionedUsers = mentions
      .filter((mention) => selectedUsers.includes(mention.login))
      .map(({ login, id, type }) => ({
        mentioned_name: login,
        user_id: id,
        user_type: type
      }));

    saveEditComment(value?.id, comment, mentionedUsers);
  };

  const isOverLimit = commentLength > maxLength;

  return (
    <Box w="100%" id="idea-comment-edit-box" display="inline" fontSize="16px">
      <Mentions
        value={comment}
        inputRef={editCommentRef}
        onChange={handleChange}
        trigger={"@"}
        data={suggestions}
      ></Mentions>
      {footerButtons(
        cancel,
        translate("common.cancel"),
        status === "commentSaving",
        save,
        translate("common.update"),
        value?.comment === comment ||
          !comment ||
          !comment?.trim() ||
          isOverLimit,
        status === "commentSaving"
      )}
    </Box>
  );
};

export { ViewComment, EditComment, CreateComment };
